import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Building2, KeyRound, MonitorSmartphone, Package, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { InlineAlert, LoadingRows } from "@/components/admin/feedback";
import { ActivationStatusBadge, LicenseStatusBadge } from "@/components/admin/status";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { apiErrorMessage } from "@/lib/api";
import { adminLicensePath, adminLicensesPath } from "@/lib/navigation";
import { useSearchQuery } from "@/lib/queries";
import type { Activation, Customer, Product, SearchResult } from "@/lib/types";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 250;
const INPUT_ID = "admin-command-input";
const LIST_ID = "admin-command-results";

type PaletteRow =
  | { kind: "license"; key: string; result: SearchResult }
  | { kind: "activation"; key: string; result: SearchResult; activation: Activation }
  | { kind: "customer"; key: string; customer: Customer }
  | { kind: "product"; key: string; product: Product };

type PaletteSection = { kind: "licenses" | "customers" | "products"; rows: PaletteRow[] };

function buildSections(results: SearchResult[]): PaletteSection[] {
  const licenseRows: PaletteRow[] = [];
  const customers = new Map<string, Customer>();
  const products = new Map<string, Product>();
  for (const result of results) {
    licenseRows.push({ kind: "license", key: `license:${result.license.id}`, result });
    for (const activation of result.activations) {
      licenseRows.push({ kind: "activation", key: `activation:${activation.id}`, result, activation });
    }
    if (!customers.has(result.customer.id)) customers.set(result.customer.id, result.customer);
    if (!products.has(result.product.id)) products.set(result.product.id, result.product);
  }
  const sections: PaletteSection[] = [];
  if (licenseRows.length) sections.push({ kind: "licenses", rows: licenseRows });
  if (customers.size) {
    sections.push({
      kind: "customers",
      rows: [...customers.values()].map((customer) => ({ kind: "customer", key: `customer:${customer.id}`, customer })),
    });
  }
  if (products.size) {
    sections.push({
      kind: "products",
      rows: [...products.values()].map((product) => ({ kind: "product", key: `product:${product.id}`, product })),
    });
  }
  return sections;
}

function rowPath(row: PaletteRow) {
  switch (row.kind) {
    case "license":
      return adminLicensePath(row.result.license.id);
    case "activation":
      return `${adminLicensePath(row.result.license.id)}?focus=activation:${row.activation.id}`;
    case "customer":
      return adminLicensesPath({ customer: row.customer.id });
    case "product":
      return adminLicensesPath({ product: row.product.id });
  }
}

function useDebouncedValue(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (!value.trim()) {
      setDebounced(value);
      return;
    }
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl overflow-hidden p-0 sm:p-0" showClose={false}>
        <DialogTitle className="sr-only">{t("commandPalette.title", "Search")}</DialogTitle>
        <PaletteBody onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS).trim();
  const searchQuery = useSearchQuery(debouncedQuery);
  const results = searchQuery.data?.results;

  const sections = useMemo(() => (results ? buildSections(results) : []), [results]);
  const rows = useMemo(() => sections.flatMap((section) => section.rows), [sections]);
  const activeRow = rows[Math.min(highlighted, rows.length - 1)];

  useEffect(() => {
    setHighlighted(0);
  }, [rows]);

  useEffect(() => {
    if (!activeRow) return;
    const element = listRef.current?.querySelector<HTMLElement>(`[data-row-key="${activeRow.key}"]`);
    element?.scrollIntoView?.({ block: "nearest" });
  }, [activeRow]);

  function activate(row: PaletteRow) {
    navigate(rowPath(row));
    onClose();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (rows.length) setHighlighted((index) => (index + 1) % rows.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (rows.length) setHighlighted((index) => (index - 1 + rows.length) % rows.length);
    } else if (event.key === "Enter") {
      if (!activeRow) return;
      event.preventDefault();
      activate(activeRow);
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  }

  const waiting = Boolean(trimmedQuery) && (trimmedQuery !== debouncedQuery || (searchQuery.isPending && !searchQuery.isError));
  const sectionLabel: Record<PaletteSection["kind"], string> = {
    licenses: t("commandPalette.licenses", "Licenses"),
    customers: t("commandPalette.customers", "Customers"),
    products: t("commandPalette.products", "Products"),
  };

  return (
    <div className="flex max-h-[min(70dvh,32rem)] flex-col">
      <div className="flex items-center gap-3 border-b border-line px-4">
        <Search className="size-4 shrink-0 text-muted" aria-hidden="true" />
        <input
          id={INPUT_ID}
          type="text"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("commandPalette.placeholder", "Search by ID, name, key suffix, or Device…")}
          aria-label={t("commandPalette.inputLabel", "Search Licenses, Customers, Products, Product Key suffixes, or Devices")}
          aria-expanded={rows.length > 0}
          aria-controls={LIST_ID}
          aria-activedescendant={activeRow ? `${LIST_ID}-${activeRow.key}` : undefined}
          aria-autocomplete="list"
          className="h-12 w-full bg-transparent text-sm text-ink outline-hidden placeholder:text-muted"
        />
        <kbd className="hidden shrink-0 rounded-sm border border-line px-1.5 py-0.5 font-mono text-[10px] text-muted sm:inline-flex" aria-hidden="true">
          Esc
        </kbd>
      </div>

      <div ref={listRef} id={LIST_ID} role="listbox" aria-label={t("commandPalette.results", "Search results")} className="console-scrollbar min-h-0 flex-1 overflow-y-auto">
        {!trimmedQuery ? (
          <p className="px-4 py-6 text-center text-sm text-muted">{t("commandPalette.hint", "Type to search. Results are bounded to 50 Licenses.")}</p>
        ) : searchQuery.isError ? (
          <div className="p-3">
            <InlineAlert
              title={t("commandPalette.loadError", "Search could not load")}
              message={apiErrorMessage(searchQuery.error)}
              onRetry={() => void searchQuery.refetch()}
            />
          </div>
        ) : waiting && rows.length === 0 ? (
          <LoadingRows count={3} />
        ) : rows.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted">{t("commandPalette.empty", "No records match.")}</p>
        ) : (
          sections.map((section) => (
            <div key={section.kind} role="group" aria-label={sectionLabel[section.kind]} className="py-1">
              <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted" aria-hidden="true">
                {sectionLabel[section.kind]}
              </p>
              {section.rows.map((row) => (
                <PaletteOption
                  key={row.key}
                  row={row}
                  active={row === activeRow}
                  onHover={() => setHighlighted(rows.indexOf(row))}
                  onSelect={() => activate(row)}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function PaletteOption({
  row,
  active,
  onHover,
  onSelect,
}: {
  row: PaletteRow;
  active: boolean;
  onHover: () => void;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  let Icon: typeof Search;
  let title: string;
  let detail: string;
  let badge: ReactNode = null;
  switch (row.kind) {
    case "license":
      Icon = KeyRound;
      title = row.result.license.id;
      detail = `${row.result.customer.name} / ${row.result.product.name}`;
      badge = (
        <LicenseStatusBadge
          status={row.result.license.status}
          validUntil={row.result.license.valid_until}
        />
      );
      break;
    case "activation":
      Icon = MonitorSmartphone;
      title = row.activation.computer_name || row.activation.installation_id;
      detail = row.activation.computer_name ? row.activation.installation_id : t("commandPalette.device", "Device");
      badge = <ActivationStatusBadge status={row.activation.status} />;
      break;
    case "customer":
      Icon = Building2;
      title = row.customer.name;
      detail = row.customer.id;
      break;
    case "product":
      Icon = Package;
      title = row.product.name;
      detail = row.product.id;
      break;
  }

  return (
    <div
      id={`${LIST_ID}-${row.key}`}
      data-row-key={row.key}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onMouseMove={onHover}
      onClick={onSelect}
      className={cn(
        "mx-1 grid cursor-default grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-3 rounded-sm px-3 py-2 text-sm text-ink",
        row.kind === "activation" && "ml-6",
        active && "bg-panel-raised outline-solid outline-2 -outline-offset-2 outline-focus forced-colors:outline-[Highlight]",
      )}
    >
      <Icon className="size-4 text-muted" aria-hidden="true" />
      <div className="min-w-0">
        <p className={cn("truncate", row.kind === "license" && "font-mono text-xs")}>{title}</p>
        <p className="truncate text-xs text-muted">{detail}</p>
      </div>
      {badge}
    </div>
  );
}
